import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import BrandGauge, { componentLabel } from "@/components/barometr/BrandGauge";
import TagCloud, { type TagCloudItem } from "@/components/barometr/TagCloud";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { freshnessLabel, painLabel, platformLabel } from "@/lib/commentLabels";
import { cn, formatNumber } from "@/lib/utils";

const WINDOWS: { id: "7d" | "30d"; label: string }[] = [
  { id: "7d", label: "7 dni" },
  { id: "30d", label: "30 dni" },
];

const CLASS_COLORS: Record<string, string> = {
  wsparcie: "#059669",
  historia_identyfikacja: "#0d9488",
  pytanie: "#2563eb",
  cta_only: "#c2410c",
  produkt_zakup: "#7c3aed",
  atak_forma: "#e11d48",
  obrona_matek: "#db2777",
  krotka_reakcja: "#64748b",
  inne: "#94a3b8",
};

function statusTone(status?: string): string {
  if (status === "lepiej") return "text-emerald-600";
  if (status === "gorzej") return "text-rose-600";
  if (status === "malo_danych") return "text-amber-600";
  return "text-muted-foreground";
}

function QuoteList({
  title,
  items,
  empty,
  accent,
}: {
  title: string;
  items: string[];
  empty: string;
  accent: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{empty}</p>
        ) : (
          items.slice(0, 5).map((text, i) => (
            <blockquote
              key={`${i}-${text.slice(0, 24)}`}
              className={cn("rounded-lg border-l-4 bg-slate-50 px-3 py-2 text-sm text-slate-700", accent)}
            >
              {text.length > 220 ? `${text.slice(0, 220)}…` : text}
            </blockquote>
          ))
        )}
      </CardContent>
    </Card>
  );
}

export default function Barometr() {
  const [window, setWindow] = useState<"7d" | "30d">("7d");
  const query = useQuery({
    queryKey: ["brand-barometer", window],
    queryFn: () => api.brandBarometer(window),
  });

  const data = query.data?.payload;
  const score = data?.score;
  const components = score?.components;

  const tagItems = useMemo((): TagCloudItem[] => {
    if (!data) return [];
    const pains: TagCloudItem[] = (data.pains || []).slice(0, 14).map((p) => ({
      id: p.id,
      label: painLabel(p.id, p.label),
      count: p.count,
      kind: "pain" as const,
    }));
    const tokens: TagCloudItem[] = (data.tokens || []).slice(0, 28).map((t) => ({
      id: t.token,
      label: t.token,
      count: t.count,
      kind: "token" as const,
    }));
    return [...pains, ...tokens];
  }, [data]);

  const classChart = useMemo(
    () =>
      (data?.classes || []).slice(0, 8).map((c) => ({
        id: c.id,
        label: c.label,
        count: c.count,
        fill: CLASS_COLORS[c.id] || "#94a3b8",
      })),
    [data],
  );

  const painChart = useMemo(
    () =>
      (data?.pains || []).slice(0, 8).map((p) => ({
        id: p.id,
        label: painLabel(p.id, p.label),
        short: p.id,
        count: p.count,
      })),
    [data],
  );

  const hardTexts = (data?.emotional.hard_questions || [])
    .map((q) => String(q.text || "").trim())
    .filter(Boolean);

  return (
    <div>
      <PageHeader
        title="Barometr marki"
        subtitle="Ton społeczności, chmura bólów i tagów, buffer emocjonalny oraz skrót lejka"
        actions={
          <div className="flex flex-wrap gap-2">
            {WINDOWS.map((w) => (
              <Button
                key={w.id}
                size="sm"
                variant={window === w.id ? "default" : "outline"}
                onClick={() => setWindow(w.id)}
              >
                {w.label}
              </Button>
            ))}
          </div>
        }
      />

      {query.isLoading && (
        <p className="text-sm text-muted-foreground">Ładuję barometr…</p>
      )}
      {query.isError && (
        <p className="text-sm text-rose-600">Nie udało się wczytać barometru.</p>
      )}

      {data && score && (
        <>
          <div className="mb-5 grid gap-4 lg:grid-cols-[220px_1fr]">
            <Card>
              <CardContent className="pt-6">
                <BrandGauge value={score.value} label={score.label} />
              </CardContent>
            </Card>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {(
                [
                  ["tone", components?.tone],
                  ["buffer", components?.buffer],
                  ["coverage", components?.coverage],
                  ["commerce", components?.commerce],
                ] as const
              ).map(([key, val]) => (
                <Card key={key}>
                  <CardContent className="pt-5">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {componentLabel(key)}
                    </p>
                    <p className="mt-1 text-2xl font-bold tabular-nums">
                      {val == null ? "n/d" : Math.round(val)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      waga {Math.round((score.weights[key] || 0) * 100)}%
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="mb-5 flex flex-wrap gap-2">
            {(data.freshness?.platforms || []).map((p) => (
              <span
                key={p.id}
                className={cn(
                  "inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium",
                  p.status === "live"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-amber-200 bg-amber-50 text-amber-800",
                )}
              >
                {platformLabel(p.id)} · {freshnessLabel(p.status)}
                {p.age_hours != null ? ` · ${Math.round(p.age_hours)}h` : ""}
              </span>
            ))}
            <span className="inline-flex rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs text-slate-600">
              Komentarze w oknie: {formatNumber(data.volume.total)}
            </span>
          </div>

          <div className="mb-5 grid gap-5 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Mix klas</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                {classChart.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Brak klas w oknie.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={classChart} layout="vertical" margin={{ left: 8, right: 12 }}>
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={88}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip formatter={(v: number) => formatNumber(v)} />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {classChart.map((row) => (
                          <Cell key={row.id} fill={row.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Ciśnienie bólów B*</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                {painChart.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Brak trafień B* w oknie.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={painChart} layout="vertical" margin={{ left: 8, right: 12 }}>
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="short"
                        width={40}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip
                        formatter={(v: number) => formatNumber(v)}
                        labelFormatter={(_, payload) => {
                          const row = payload?.[0]?.payload as { label?: string } | undefined;
                          return row?.label || "";
                        }}
                      />
                      <Bar dataKey="count" fill="#c2410c" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="mb-5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">
                Chmura tagów (bóle B* + tokeny)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TagCloud items={tagItems} />
            </CardContent>
          </Card>

          <div className="mb-5 grid gap-5 lg:grid-cols-3">
            <QuoteList
              title={`Trudne pytania (${hardTexts.length})`}
              items={hardTexts}
              empty="Brak dłuższych pytań w snapshotcie."
              accent="border-l-blue-500"
            />
            <QuoteList
              title={`Wsparcie (${data.emotional.supportive_count})`}
              items={data.emotional.supportive}
              empty="Brak sygnałów wsparcia w regułach buffera."
              accent="border-l-emerald-500"
            />
            <QuoteList
              title={`Kwarantanna (${data.emotional.quarantined_count})`}
              items={data.emotional.quarantined}
              empty="Brak trafień słów negatywnych."
              accent="border-l-rose-500"
            />
          </div>

          <div className="mb-5 grid gap-5 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Pokrycie odpowiedzi</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-700">
                  Reply ownera:{" "}
                  <span className="font-semibold tabular-nums">
                    {Math.round((data.coverage.owner_reply_pct || 0) * 100)}%
                  </span>{" "}
                  postów ({data.coverage.owner_reply_posts}/{data.coverage.total_posts})
                </p>
                <div className="space-y-2">
                  {(data.coverage.items || []).map((v) => (
                    <div key={v.platform} className="flex justify-between text-sm">
                      <span className="text-slate-700">{platformLabel(v.platform)}</span>
                      <span className="font-mono text-xs text-slate-500">
                        {v.days_with_comments} dni · braki {v.missing_days} ·{" "}
                        {formatNumber(v.comments_in_range)}
                      </span>
                    </div>
                  ))}
                </div>
                {(data.coverage.recommendations || []).slice(0, 2).map((r) => (
                  <p key={r} className="text-xs text-muted-foreground">
                    {r}
                  </p>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Lejek (skrót)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div>
                    <p className="text-xs text-muted-foreground">CTA</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {Math.round((data.funnel.cta_pct || 0) * 100)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Do akcji</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {formatNumber(data.funnel.action)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Reach +7d</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {formatNumber(data.funnel.reach_7d.reach_gained)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Publikacje 7d</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {formatNumber(data.funnel.reach_7d.publications)}
                    </p>
                  </div>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  {(data.funnel.sales.cards || []).map((card) => (
                    <div
                      key={card.id || card.label}
                      className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                    >
                      <p className="text-xs text-muted-foreground">{card.label}</p>
                      <p className="text-base font-semibold tabular-nums">
                        {card.value_display || "n/d"}
                      </p>
                      <p className={cn("text-xs", statusTone(card.status))}>
                        {card.compare_text || ""}
                      </p>
                    </div>
                  ))}
                </div>
                {(data.funnel.sales.verdicts || []).slice(0, 2).map((v) => (
                  <p key={v.id || v.text} className="text-sm text-slate-700">
                    {v.text}
                  </p>
                ))}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button asChild size="sm" variant="outline">
                    <Link to="/komentarze">Komentarze</Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/sprzedaz">Sprzedaż</Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/podsumowanie">Podsumowanie</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
